import { useCallback, useEffect, useState } from "react";

/**
 * The `beforeinstallprompt` event is not part of the standard TS DOM libs, so
 * we declare a minimal shape for the fields we use.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEY = "kw_app_install_prompted";

/** Delay before the prompt becomes eligible, so it doesn't fight first paint. */
const SHOW_DELAY_MS = 3000;

/**
 * Detects iOS Safari (iPhone, iPad, iPod). Modern iPads report a `Macintosh`
 * UA, so we also treat a touch-capable macOS device as iOS.
 */
function detectIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
  const isMacWithTouch =
    /macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  return isIOSDevice || isMacWithTouch;
}

/** True when the app is already running installed (home screen / standalone). */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS exposes this when launched from the home screen.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export type InstallPlatform = "native" | "ios";

/**
 * Captures the browser's `beforeinstallprompt` event (Chrome/Edge/Android) and
 * exposes a way to trigger the native install prompt.
 *
 * iOS Safari never fires `beforeinstallprompt`, so we detect iOS directly and
 * expose `platform: "ios"` so the UI can render manual "Add to Home Screen"
 * instructions instead of the native prompt.
 *
 * Shows only once per device: once the prompt has been presented (clicked or
 * dismissed), a localStorage flag prevents it from reappearing. A short delay
 * avoids showing it before the page has settled.
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  // `ready` flips true SHOW_DELAY_MS after mount; gates the UI so we don't
  // flash the banner during initial render.
  const [ready, setReady] = useState(false);
  const ios = detectIOS() && !isStandalone();

  useEffect(() => {
    // Already prompted this device — don't capture again.
    if (localStorage.getItem(STORAGE_KEY)) return;

    // If the app is already running standalone (installed), do nothing.
    if (isStandalone()) return;

    const timer = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);

    const handler = (e: Event) => {
      // Prevent the mini-infobar from showing on early Chrome; we use our own UI.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  /** Once we've captured the event, the app is installable via native prompt. */
  const canInstall = ready && (deferredPrompt !== null || ios);
  const platform: InstallPlatform = ios ? "ios" : "native";

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDeferredPrompt(null);
    setReady(false);
  }, []);

  /** Triggers the native prompt; resolves to true if the user accepted. */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // Remember we've shown it, regardless of the outcome (show only once).
    localStorage.setItem(STORAGE_KEY, "1");
    setDeferredPrompt(null);
    return choice.outcome === "accepted";
  }, [deferredPrompt]);

  return { canInstall, platform, promptInstall, dismiss };
}
